import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, ShoppingCart, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, fmtQty } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, opts } from "@/components/Field";
import { ProductDialog } from "@/components/dialogs/ProductDialog";
import { ShoppingDialog } from "@/components/dialogs/ShoppingDialog";

const ALERTS = [{ value: "expired", label: "Scaduti" }, { value: "expiring", label: "In scadenza questo mese" }, { value: "low", label: "Sotto scorta minima" }];
const SORTS = [{ value: "name", label: "Nome" }, { value: "expiry", label: "Scadenza più vicina" }, { value: "quantity", label: "Quantità" }, { value: "recent", label: "Più recenti" }];

export const ProductFlags = ({ p }) => (
  <>
    {p.expired && <StatusBadge label="Scaduto" tone="danger" />}
    {p.expiring && <StatusBadge label="In scadenza" tone="warning" />}
    {p.low_stock && <StatusBadge label="Scorta bassa" tone="warning" />}
    {p.status === "archived" && <StatusBadge label="Archiviato" />}
  </>
);

export default function InventoryPage() {
  const { assoc, can } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [dialog, setDialog] = useState(false);
  const [shopFor, setShopFor] = useState(null);
  const get = (k, d = "") => sp.get(k) || d;
  const setParam = (k) => (v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    if (k !== "page") n.delete("page");
    setSp(n);
  };
  const page = Number(get("page", "1"));
  const qs = new URLSearchParams({ q: get("q"), category: get("category"), location: get("location"), alert: get("alert"), status: get("status", "active"), sort: get("sort", "name"), page, page_size: 25 });
  const { data, reload } = useFetch(`/products?${qs}`);
  const summary = useFetch("/inventory/summary", [data]).data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <>
      <PageHeader title="Inventario" subtitle={summary ? `${summary.products} prodotti attivi` : ""}>
        {can("inventory", "create") && <Button data-testid="inventory-new-product" className="h-11" onClick={() => setDialog(true)}><Plus className="h-4 w-4 mr-1" /> Nuovo prodotto</Button>}
      </PageHeader>

      {summary && (
        <div className="flex flex-wrap gap-2" data-testid="inventory-alert-chips">
          {[["expired", "Scaduti", summary.expired, "bg-red-50 text-red-800 border-red-200"], ["expiring", "In scadenza", summary.expiring, "bg-amber-50 text-amber-900 border-amber-200"], ["low", "Scorta bassa", summary.low_stock, "bg-amber-50 text-amber-900 border-amber-200"]].map(([k, l, n, c]) => (
            <button key={k} data-testid={`inventory-chip-${k}`} onClick={() => setParam("alert")(get("alert") === k ? "" : k)} className={`rounded-full border px-4 h-10 text-sm font-semibold ${c} ${get("alert") === k ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}>{l}: {n}</button>
          ))}
        </div>
      )}

      <Card className="p-4 space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); setParam("q")(q); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="inventory-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice o codice a barre" className="h-11 pl-9" />
          </div>
          <Button data-testid="inventory-search-button" type="submit" variant="outline" className="h-11">Cerca</Button>
        </form>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Sel testId="inventory-filter-category" value={get("category")} onChange={setParam("category")} options={opts(assoc?.categories)} placeholder="Tutte le categorie" />
          <Sel testId="inventory-filter-location" value={get("location")} onChange={setParam("location")} options={opts(assoc?.locations)} placeholder="Tutte le ubicazioni" />
          <Sel testId="inventory-filter-alert" value={get("alert")} onChange={setParam("alert")} options={ALERTS} placeholder="Tutti gli stati" />
          <Sel testId="inventory-filter-status" value={get("status", "active")} onChange={setParam("status")} options={[{ value: "active", label: "Attivi" }, { value: "archived", label: "Archiviati" }]} />
          <Sel testId="inventory-sort" value={get("sort", "name")} onChange={setParam("sort")} options={SORTS} />
        </div>
      </Card>

      {data && data.items.length === 0 ? <Empty testId="inventory-empty" text="Nessun prodotto trovato." /> : (
        <Card>
          <ul className="divide-y divide-slate-100" data-testid="inventory-list">
            {data?.items.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50">
                <button data-testid={`product-row-${p.id}`} onClick={() => nav(`/inventario/${p.id}`)} className="flex-1 min-w-0 text-left">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-semibold text-slate-900">{p.name}</span>
                    <ProductFlags p={p} />
                  </div>
                  <div className="text-sm text-slate-500">{[p.category, p.location, p.nearest_expiry && `scad. ${fmtDate(p.nearest_expiry)}`].filter(Boolean).join(" · ")}</div>
                </button>
                <div className="text-right shrink-0">
                  <div className="font-heading text-lg font-bold text-slate-900">{fmtQty(p.quantity)}</div>
                  <div className="text-xs text-slate-500">{p.unit}{p.min_stock != null && ` · min ${fmtQty(p.min_stock)}`}</div>
                </div>
                {p.low_stock && can("shopping", "create") && (
                  <Button data-testid={`product-add-shopping-${p.id}`} variant="outline" size="icon" className="h-11 w-11 shrink-0" title="Aggiungi alla spesa" onClick={() => setShopFor(p)}><ShoppingCart className="h-5 w-5" /></Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="inventory-pagination">
          <Button variant="outline" className="h-11" disabled={page <= 1} onClick={() => setParam("page")(String(page - 1))} data-testid="inventory-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm">Pagina {page} di {pages}</span>
          <Button variant="outline" className="h-11" disabled={page >= pages} onClick={() => setParam("page")(String(page + 1))} data-testid="inventory-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      <ProductDialog open={dialog} onOpenChange={setDialog} onSaved={(p) => nav(`/inventario/${p.id}`)} />
      <ShoppingDialog open={!!shopFor} onOpenChange={(o) => !o && setShopFor(null)} onSaved={reload}
        preset={shopFor ? { name: shopFor.name, unit: shopFor.unit, product_id: shopFor.id, quantity: String(Math.max(1, (shopFor.min_stock || 0) - (shopFor.quantity || 0))) } : null} />
    </>
  );
}
